import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Mail, Phone, Lock, Eye, EyeOff, MapPin, Home, Zap, CreditCard, Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const STEPS = [
    { id: 1, title: 'Basic Details', icon: User },
    { id: 2, title: 'Shipping & Solar Info', icon: Home },
    { id: 3, title: 'Billing Info', icon: CreditCard }
];

const MultiStepRegister = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        // Step 1: Basic Details
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',

        // Step 2: Shipping & Solar Info
        address: {
            street: '',
            city: '',
            state: '',
            pincode: '',
            landmark: ''
        },
        solarInfo: {
            monthlyElectricityBill: '',
            homeSpaceSqFt: '',
            roofType: 'flat',
            propertyType: 'residential'
        },

        // Step 3: Billing Info
        billingAddress: {
            sameAsShipping: true
        },
        gstNumber: '',
        preferredPaymentMethod: 'razorpay'
    });

    // Load saved progress from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('user_registration_progress');
        if (saved) {
            try {
                const data = JSON.parse(saved);
                setFormData(data.formData || formData);
                setCurrentStep(data.currentStep || 1);
            } catch (e) {
                console.error('Failed to load saved progress:', e);
            }
        }
    }, []);

    // Save progress to localStorage
    useEffect(() => {
        localStorage.setItem('user_registration_progress', JSON.stringify({
            formData,
            currentStep
        }));
    }, [formData, currentStep]);

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const updateNestedField = (parent, field, value) => {
        setFormData(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [field]: value
            }
        }));
    };

    const validateStep1 = () => {
        if (!formData.name.trim()) {
            toast.error('Please enter your name');
            return false;
        }
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            toast.error('Please enter a valid email');
            return false;
        }
        if (!formData.phone.trim() || !/^[6-9][0-9]{9}$/.test(formData.phone)) {
            toast.error('Please enter a valid 10-digit phone number');
            return false;
        }
        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return false;
        }
        return true;
    };

    const validateStep2 = () => {
        if (!formData.address.street.trim()) {
            toast.error('Please enter your street address');
            return false;
        }
        if (!formData.address.city.trim()) {
            toast.error('Please enter your city');
            return false;
        }
        if (!formData.address.state.trim()) {
            toast.error('Please enter your state');
            return false;
        }
        if (!formData.address.pincode.trim() || !/^[1-9][0-9]{5}$/.test(formData.address.pincode)) {
            toast.error('Please enter a valid 6-digit pincode');
            return false;
        }
        if (!formData.solarInfo.monthlyElectricityBill || formData.solarInfo.monthlyElectricityBill <= 0) {
            toast.error('Please enter your monthly electricity bill');
            return false;
        }
        if (!formData.solarInfo.homeSpaceSqFt || formData.solarInfo.homeSpaceSqFt <= 0) {
            toast.error('Please enter your home space in square feet');
            return false;
        }
        return true;
    };

    const validateStep3 = () => {
        // GST number is optional, but if provided, validate format
        if (formData.gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/.test(formData.gstNumber.toUpperCase())) {
            toast.error('Invalid GST number format');
            return false;
        }
        return true;
    };

    const handleNext = () => {
        let isValid = false;

        if (currentStep === 1) {
            isValid = validateStep1();
        } else if (currentStep === 2) {
            isValid = validateStep2();
        } else if (currentStep === 3) {
            isValid = validateStep3();
        }

        if (isValid) {
            if (currentStep < 3) {
                setCurrentStep(currentStep + 1);
            } else {
                handleComplete();
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleComplete = () => {
        // Clear saved progress
        localStorage.removeItem('user_registration_progress');

        // Pass data to parent component
        onComplete(formData);
    };

    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, label: '', color: '' };
        let strength = 0;
        if (password.length >= 6) strength++;
        if (password.length >= 10) strength++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^a-zA-Z0-9]/.test(password)) strength++;

        if (strength <= 2) return { strength, label: 'Weak', color: 'bg-red-500' };
        if (strength <= 3) return { strength, label: 'Medium', color: 'bg-yellow-500' };
        return { strength, label: 'Strong', color: 'bg-green-500' };
    };

    const passwordStrength = getPasswordStrength(formData.password);

    return (
        <div className="w-full max-w-3xl mx-auto">
            {/* Progress Bar */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    {STEPS.map((step, index) => {
                        const Icon = step.icon;
                        const isActive = currentStep === step.id;
                        const isCompleted = currentStep > step.id;

                        return (
                            <React.Fragment key={step.id}>
                                <div className="flex flex-col items-center gap-2 flex-1">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isCompleted ? 'bg-green-500 text-white' :
                                            isActive ? 'bg-primary text-white' :
                                                'bg-muted text-muted-foreground'
                                        }`}>
                                        {isCompleted ? <Check className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                                    </div>
                                    <span className={`text-sm font-medium hidden sm:block ${isActive ? 'text-foreground' : 'text-muted-foreground'
                                        }`}>
                                        {step.title}
                                    </span>
                                </div>
                                {index < STEPS.length - 1 && (
                                    <div className={`h-1 flex-1 mx-2 rounded transition-all ${currentStep > step.id ? 'bg-green-500' : 'bg-muted'
                                        }`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Form Steps */}
            <div className="bg-card rounded-2xl border p-6 md:p-8">
                <AnimatePresence mode="wait">
                    {/* Step 1: Basic Details */}
                    {currentStep === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Basic Details</h2>
                                <p className="text-muted-foreground">Let's start with your basic information</p>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name *</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="name"
                                            placeholder="John Doe"
                                            value={formData.name}
                                            onChange={e => updateFormData('name', e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email *</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={formData.email}
                                            onChange={e => updateFormData('email', e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone Number *</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="phone"
                                            type="tel"
                                            placeholder="9876543210"
                                            maxLength={10}
                                            value={formData.phone}
                                            onChange={e => updateFormData('phone', e.target.value.replace(/\D/g, ''))}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password *</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={e => updateFormData('password', e.target.value)}
                                            className="pl-10 pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    {formData.password && (
                                        <div className="space-y-1">
                                            <div className="flex gap-1">
                                                {[1, 2, 3, 4, 5].map(i => (
                                                    <div
                                                        key={i}
                                                        className={`h-1 flex-1 rounded transition-all ${i <= passwordStrength.strength ? passwordStrength.color : 'bg-muted'
                                                            }`}
                                                    />
                                                ))}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Password strength: <span className="font-medium">{passwordStrength.label}</span>
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="confirmPassword"
                                            type="password"
                                            placeholder="••••••••"
                                            value={formData.confirmPassword}
                                            onChange={e => updateFormData('confirmPassword', e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 2: Shipping & Solar Info */}
                    {currentStep === 2 && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Shipping & Solar Information</h2>
                                <p className="text-muted-foreground">Tell us about your location and solar needs</p>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="street">Street Address *</Label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="street"
                                            placeholder="123 Main Street, Apartment 4B"
                                            value={formData.address.street}
                                            onChange={e => updateNestedField('address', 'street', e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="city">City *</Label>
                                        <Input
                                            id="city"
                                            placeholder="Mumbai"
                                            value={formData.address.city}
                                            onChange={e => updateNestedField('address', 'city', e.target.value)}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="state">State *</Label>
                                        <Input
                                            id="state"
                                            placeholder="Maharashtra"
                                            value={formData.address.state}
                                            onChange={e => updateNestedField('address', 'state', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="pincode">Pincode *</Label>
                                        <Input
                                            id="pincode"
                                            placeholder="400001"
                                            maxLength={6}
                                            value={formData.address.pincode}
                                            onChange={e => updateNestedField('address', 'pincode', e.target.value.replace(/\D/g, ''))}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="landmark">Landmark (Optional)</Label>
                                        <Input
                                            id="landmark"
                                            placeholder="Near City Mall"
                                            value={formData.address.landmark}
                                            onChange={e => updateNestedField('address', 'landmark', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="border-t pt-4 mt-6">
                                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                                        <Zap className="w-5 h-5 text-primary" />
                                        Solar Requirements
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="electricityBill">Monthly Electricity Bill (₹) *</Label>
                                            <Input
                                                id="electricityBill"
                                                type="number"
                                                placeholder="5000"
                                                value={formData.solarInfo.monthlyElectricityBill}
                                                onChange={e => updateNestedField('solarInfo', 'monthlyElectricityBill', e.target.value)}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="homeSpace">Home Space (sq ft) *</Label>
                                            <Input
                                                id="homeSpace"
                                                type="number"
                                                placeholder="1500"
                                                value={formData.solarInfo.homeSpaceSqFt}
                                                onChange={e => updateNestedField('solarInfo', 'homeSpaceSqFt', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="roofType">Roof Type *</Label>
                                            <select
                                                id="roofType"
                                                value={formData.solarInfo.roofType}
                                                onChange={e => updateNestedField('solarInfo', 'roofType', e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background"
                                            >
                                                <option value="flat">Flat Roof</option>
                                                <option value="sloped">Sloped Roof</option>
                                            </select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="propertyType">Property Type *</Label>
                                            <select
                                                id="propertyType"
                                                value={formData.solarInfo.propertyType}
                                                onChange={e => updateNestedField('solarInfo', 'propertyType', e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background"
                                            >
                                                <option value="residential">Residential</option>
                                                <option value="commercial">Commercial</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 3: Billing Info */}
                    {currentStep === 3 && (
                        <motion.div
                            key="step3"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Billing Information</h2>
                                <p className="text-muted-foreground">Payment and billing details</p>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-muted/50 rounded-lg p-4 border">
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            id="sameAsShipping"
                                            checked={formData.billingAddress.sameAsShipping}
                                            onChange={e => updateNestedField('billingAddress', 'sameAsShipping', e.target.checked)}
                                            className="mt-1"
                                        />
                                        <div>
                                            <Label htmlFor="sameAsShipping" className="cursor-pointer">
                                                Billing address same as shipping address
                                            </Label>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {formData.address.street}, {formData.address.city}, {formData.address.state} - {formData.address.pincode}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="gstNumber">GST Number (Optional)</Label>
                                    <Input
                                        id="gstNumber"
                                        placeholder="22AAAAA0000A1Z5"
                                        value={formData.gstNumber}
                                        onChange={e => updateFormData('gstNumber', e.target.value.toUpperCase())}
                                        maxLength={15}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        For businesses only. Format: 22AAAAA0000A1Z5
                                    </p>
                                </div>

                                <div className="border-t pt-4 mt-6">
                                    <h3 className="font-semibold mb-4">Payment Method</h3>
                                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                                                <CreditCard className="w-6 h-6 text-primary" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium">Razorpay</p>
                                                <p className="text-sm text-muted-foreground">Secure online payment gateway</p>
                                            </div>
                                            <Check className="w-5 h-5 text-primary" />
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-3">
                                        💳 We accept all major credit/debit cards, UPI, and net banking through Razorpay
                                    </p>
                                </div>

                                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
                                    <p className="text-sm text-blue-900 dark:text-blue-100">
                                        <strong>Note:</strong> Your registration will be completed after this step. You can proceed to browse and add products to your cart. Payment will be processed during checkout.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="flex gap-3 mt-8">
                    {currentStep > 1 && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleBack}
                            className="flex-1"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                    )}
                    <Button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 btn-primary"
                    >
                        {currentStep === 3 ? 'Complete Registration' : 'Next'}
                        {currentStep < 3 && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MultiStepRegister;
